/**
 * Massnahme Dialog Component
 * 
 * Dialog for creating and editing Maßnahmen (projects/measures).
 * Extracted from Shifts.jsx for modularity.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { MasterMeasureMultiSelect } from './MasterMeasureMultiSelect';
import { WeekdayTimesEditor } from './WeekdayTimesEditor';
import { PauseEditor } from './PauseEditor';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { WeekPicker } from '../../components/ui/week-picker';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Target, Loader2, MapPin, Clock, CalendarDays } from 'lucide-react';

const MassnahmeDialog = ({
  open,
  onOpenChange,
  selectedMassnahme,
  formData,
  setFormData,
  locations,
  saving,
  onSave,
}) => {
  const [weekMode, setWeekMode] = useState('continuous');

  // Sync mode with incoming data when dialog opens
  useEffect(() => {
    if (open) {
      setWeekMode(formData.scheduled_weeks?.length > 0 ? 'scheduled' : 'continuous');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedMassnahme]);

  const handleModeChange = (mode) => {
    setWeekMode(mode);
    if (mode === 'continuous') {
      setFormData({ ...formData, scheduled_weeks: [] });
    } else {
      setFormData({ ...formData, excluded_weeks: [] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {selectedMassnahme ? 'Maßnahme bearbeiten' : 'Neue Maßnahme'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2 sm:pt-4">
          <div className="space-y-2">
            <Label>Standort *</Label>
            <Select 
              value={formData.location_id} 
              onValueChange={(v) => setFormData({ ...formData, location_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Standort wählen..." />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {loc.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Name *</Label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="z.B. Berufsausbildung in außerbetrieblichen Einrichtungen"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kürzel *</Label>
              <Input 
                value={formData.short_code}
                onChange={(e) => setFormData({ ...formData, short_code: e.target.value.toUpperCase() })}
                placeholder="z.B. BaE"
                maxLength={10}
              />
            </div>
            <div className="space-y-2">
              <Label>Teilnehmeranzahl</Label>
              <Input 
                type="number"
                min="0"
                value={formData.teilnehmer_anzahl}
                onChange={(e) => setFormData({ ...formData, teilnehmer_anzahl: e.target.value })}
                placeholder="z.B. 15"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Farbe</Label>
            <div className="flex items-center gap-2">
              <Input 
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input 
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="font-mono flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Beschreibung</Label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optionale Beschreibung..."
              rows={2}
            />
          </div>

          {/* Durchführungszeit */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Durchführungszeit
            </Label>
            <WeekdayTimesEditor
              formData={formData}
              setFormData={setFormData}
              locationName={(locations.find((l) => l.id === formData.location_id) || {}).name || ''}
            />
            <div className="mt-2 text-xs rounded-md border border-sky-300 bg-sky-50 text-sky-800 px-3 py-2" data-testid="massnahme-default-hours-hint">
              <strong>ℹ Hinweis:</strong> Wenn keine Durchführungszeit hinterlegt ist, zählt der Monats-Export für Ganztags-Schichten automatisch <strong>8 Std. (08:00–16:00)</strong> als Standard-Vollzeit.
            </div>
          </div>

          {/* Pause */}
          <PauseEditor formData={formData} setFormData={setFormData} />

          {/* Laufzeit */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium mb-3 block">Laufzeit (optional)</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Startdatum</Label>
                <DatePickerDE
                  value={formData.start_date || ''}
                  onChange={(v) => setFormData({ ...formData, start_date: v })}
                  placeholder="Startdatum"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Enddatum</Label>
                <DatePickerDE
                  value={formData.end_date || ''}
                  onChange={(v) => setFormData({ ...formData, end_date: v })}
                  placeholder="Enddatum"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Lassen Sie die Felder leer für eine unbegrenzte Laufzeit
            </p>
          </div>

          {/* Wochen-Rhythmus */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium mb-3 flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Wochen-Rhythmus
            </Label>
            <RadioGroup value={weekMode} onValueChange={handleModeChange} className="space-y-2 mb-4">
              <button
                type="button"
                onClick={() => handleModeChange('continuous')}
                className="flex items-start space-x-2 text-left w-full hover:bg-muted/40 rounded-md p-2 transition-colors"
              >
                <RadioGroupItem value="continuous" id="week-mode-continuous" className="mt-0.5 pointer-events-none" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Durchgehend (mit optionalen Ausnahmen)</div>
                  <p className="text-xs text-muted-foreground">
                    Maßnahme läuft in allen Wochen der Laufzeit, außer explizit ausgeschlossenen.
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('scheduled')}
                className="flex items-start space-x-2 text-left w-full hover:bg-muted/40 rounded-md p-2 transition-colors"
              >
                <RadioGroupItem value="scheduled" id="week-mode-scheduled" className="mt-0.5 pointer-events-none" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Nur in ausgewählten Wochen</div>
                  <p className="text-xs text-muted-foreground">
                    Ideal für sporadische Maßnahmen (z.B. Prüfungswochen, Workshops).
                  </p>
                </div>
              </button>
            </RadioGroup>

            {weekMode === 'scheduled' ? (
              <WeekPicker
                value={formData.scheduled_weeks || []}
                onChange={(weeks) => setFormData({ ...formData, scheduled_weeks: weeks })}
                label="Geplante Kalenderwochen"
                helpText="Klicken Sie auf die KW, in denen die Maßnahme stattfindet."
              />
            ) : (
              <WeekPicker
                value={formData.excluded_weeks || []}
                onChange={(weeks) => setFormData({ ...formData, excluded_weeks: weeks })}
                label="Ausgeschlossene Kalenderwochen (optional)"
                helpText="Wochen, in denen die Maßnahme ausfällt (z.B. Betriebsferien)."
              />
            )}
          </div>

          <MasterMeasureMultiSelect
            value={formData.parent_measure_ids || []}
            onChange={(ids) => setFormData({ ...formData, parent_measure_ids: ids })}
          />

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <label htmlFor="is_active" className="text-sm">Maßnahme ist aktiv</label>
          </div>

          <div className="flex items-start space-x-2 p-2 rounded-md bg-secondary/30">
            <Checkbox
              id="hide_in_tv"
              checked={!!formData.hide_in_tv}
              onCheckedChange={(checked) => setFormData({ ...formData, hide_in_tv: !!checked })}
              data-testid="massnahme-hide-in-tv"
            />
            <label htmlFor="hide_in_tv" className="text-sm leading-tight cursor-pointer">
              In <strong>TV-Ansicht</strong> ausblenden
              <span className="block text-xs text-muted-foreground mt-0.5">
                Maßnahme wird weiterhin in der Planung angezeigt, aber nicht mehr auf öffentlichen TV-Bildschirmen.
              </span>
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Abbrechen</Button>
            <Button onClick={onSave} disabled={saving} className="w-full sm:w-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MassnahmeDialog;
